import { useIntervalFn, watchOnce } from '@vueuse/core';
import uniq from 'lodash/uniq';
import { defineStore, storeToRefs } from 'pinia';
import { watch } from 'vue';
import { useFavoriteCount } from '@/composable/useFavoriteCount';
import { useSessionStorage } from '@/composable/useSessionStorage';
import { useState } from '@/composable/useState';
import MapWithDefault from '@/core/lib/mapWithDefault';
import { useSimpleEvents } from '@/core/oddsApi/composable/useSimpleEvents';
import { type SportType } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';

export interface IFavorite {
    sportType: SportType;
    eventIds: number[];
    leagueIds: number[];
}

export const useFavoriteStore = defineStore('Favorite', () => {
    const [favorites] = useState(new MapWithDefault<SportType, IFavorite>(sportType => ({
        sportType,
        eventIds: [],
        leagueIds: [],
    })));
    const { favoritesSportList, favoriteMarketListMap, updateCount } = useFavoriteCount(favorites);
    const { accountId } = storeToRefs(useCustomerStore());

    const [sessionFavorites, setSessionFavorites] = useSessionStorage<IFavorite[]>(
        `favorites:${accountId.value}`,
        [],
        {
            decode: v => JSON.parse(v),
            encode: v => JSON.stringify(v),
        },
    );

    const getFavorites = () => {
        sessionFavorites.value.forEach((x) => {
            favorites.value.set(x.sportType, {
                sportType: x.sportType,
                eventIds: x.eventIds ?? [],
                leagueIds: x.leagueIds ?? [],
            });
        });
    };
    getFavorites();

    const updateFavorites = () => {
        const result = favorites.value.filter(x => x.eventIds.length !== 0 || x.leagueIds.length !== 0);
        setSessionFavorites(result);
    };
    watch(favorites, updateFavorites, { deep: true });

    const getFavoriteBySport = (sportType: SportType) => favorites.value.get(sportType);

    const isFavoriteEvent = (sport: SportType, eventId: number) => getFavoriteBySport(sport).eventIds.includes(eventId);

    const isFavoriteLeague = (sport: SportType, leagueId: number) => getFavoriteBySport(sport).leagueIds.includes(leagueId);

    const addEvent = (sport: SportType, eventId: number) => {
        const sportFavorite = getFavoriteBySport(sport);
        sportFavorite.eventIds = uniq([...sportFavorite.eventIds, eventId]);
        updateCount();
    };
    const removeEvent = (sport: SportType, eventId: number) => {
        const sportFavorite = getFavoriteBySport(sport);
        sportFavorite.eventIds = sportFavorite.eventIds.filter(e => e !== eventId);
        updateCount();
    };

    const addLeague = (sport: SportType, leagueId: number) => {
        const sportFavorite = getFavoriteBySport(sport);
        sportFavorite.leagueIds = uniq([...sportFavorite.leagueIds, leagueId]);
        updateCount();
    };
    const removeLeague = (sport: SportType, leagueId: number) => {
        const sportFavorite = getFavoriteBySport(sport);
        sportFavorite.leagueIds = sportFavorite.leagueIds.filter(e => e !== leagueId);
        updateCount();
    };

    function cleanNotRunningEvents() {
        const handleCleanEvents = (sportType: SportType, eventIds: number[], simpleEvents: number[]) => {
            eventIds.forEach((eventId) => {
                if (!simpleEvents.includes(eventId)) {
                    removeEvent(sportType, eventId);
                }
            });
        };

        favorites.value.forEach((x) => {
            if (x.eventIds.length === 0) return;

            const { simpleEvents, loaded } = useSimpleEvents({
                sportType: x.sportType,
                eventIds: x.eventIds,
            });

            watchOnce(loaded, () => handleCleanEvents(x.sportType, x.eventIds, simpleEvents.value));
        });
    }

    const ONE_HOUR = 60 * 60 * 1000;
    useIntervalFn(() => cleanNotRunningEvents(), ONE_HOUR, { immediateCallback: true });

    return {
        favorites,
        isFavoriteEvent,
        isFavoriteLeague,
        addEvent,
        removeEvent,
        addLeague,
        removeLeague,
        getFavoriteBySport,
        favoritesSportList,
        favoriteMarketListMap,
    };
});
